import { QAndASides } from 'router/subrouters/DataRoom/models/QAndA';
import { ObjectValues } from 'types';

export type BaseMixpanelEvent = {
  pageUrl: string;
};

export type LogoutProps = BaseMixpanelEvent;

export type ErrorViewedProps = BaseMixpanelEvent & {
  id: string;
  errorCode: string;
};

export type PageViewedProps = BaseMixpanelEvent & {
  page: string;
};

export type DocumentEventProps = BaseMixpanelEvent & {
  documentName: string;
  dataRoom?: string;
  timespent?: string;
  timespentInSeconds?: number;
};

export type SearchInitiatedEventProps = BaseMixpanelEvent & {
  searchedTerm: string;
};

export type NotificationViewedInitiatedEventProps = BaseMixpanelEvent & {
  notificationText: string;
  notificationCode: number;
};

export type SecureLinkCreationInitiatedEventProps = BaseMixpanelEvent & {
  linkName: string;
  linkAccess: string;
  linkExpiration: string;
  linkProtection: string;
  remoteTermination: string;
};

export type LeadStatusUpdateInitiatedEventProps = BaseMixpanelEvent & {
  dealId: string;
  sourceStatus: string;
  destinationStatus: string;
  leadId: string;
};

export type ListingWatchedInitiatedEventProps = BaseMixpanelEvent & {
  dealId: string;
};

export type ListingVisibilityChangeInitiatedEventProps = BaseMixpanelEvent & {
  listingName: string;
  designatedVisibility: string;
};

export type ListingCreationInitiatedEventProps = BaseMixpanelEvent & {
  dealId: string;
  industry: string;
  isPaid: boolean;
  listingPackage: string;
  listingType: string;
  mediaPackage?: string;
  outreach?: string;
};

export type MobileVerificationInitiatedEventProps = BaseMixpanelEvent;

export const ENQUIRY_ORIGIN = {
  MANUAL: 'manual',
  ORGANIC: 'organic',
};

type EnquiryOrigin = ObjectValues<typeof ENQUIRY_ORIGIN>;

export type EnquiryCreationInitiatedEventProps = BaseMixpanelEvent & {
  dealId: string;
  dealName: string;
  enquiryOrigin: EnquiryOrigin;
};

export type EnquiryWithdrawalInitiatedEventProps = BaseMixpanelEvent & {
  dealId: string;
  leadId: string;
};

export const SIGNUP_METHOD = {
  EMAIL: 'email',
  PHONE_NUMBER: 'phone_number',
} as const;

export type SignupMethod = ObjectValues<typeof SIGNUP_METHOD>;

export const SIGNUP_SOURCE = {
  ORGANIC: 'organic',
  LEAD_INVITATION: 'lead_invitation',
  BROKER_INVITATION: 'broker_invitation',
  ENTERPRISE_INVITATION: 'enterprise_invitation',
} as const;

export type SignupSource = ObjectValues<typeof SIGNUP_SOURCE>;

export type SignupInitiatedProps = BaseMixpanelEvent & {
  socialSignup: boolean;
  signupSource: SignupSource;
  signupMethod?: SignupMethod;
};

export type DealCreationInitiatedEventProps = BaseMixpanelEvent & {
  dealId: string;
  dealName: string;
};

export type SoftSignalInitiatedEventProps = BaseMixpanelEvent & {
  dealId: string;
  dataRoomId: string;
  documentId: string;
  reaction: string;
};

export type QuickFileReleaseInitiatedEventProps = BaseMixpanelEvent & {
  dealId: string;
  action: string;
  fileName: string;
};

export type DocumentPublishedEventProps = BaseMixpanelEvent & {
  documentName: string;
  documentType: string;
  dataRoomId: string;
  dataRoomName: string;
};

export type QuestionSubmissionInitiatedEventProps = BaseMixpanelEvent & {
  questionSubject: string;
  questionContent: string;
  questionPriority?: string;
  addedToFAQ?: boolean;
  assignee?: string;
  buysideBehalf?: string;
  relatedDocument?: string;
  posingQuestionSide: QAndASides;
};

export type QuestionReplyInitiatedEventProps = BaseMixpanelEvent & {
  questionId: string;
  questionContent: string;
  answerContent: string;
  addedToFAQ: boolean;
  approvalModus: ApprovalModus;
};

export const ANSWER_REVIEW_OUTPUT = {
  ACCEPTED: 'accepted',
  REJECTED: 'rejected',
} as const;

export type AnswerReviewOutput = ObjectValues<typeof ANSWER_REVIEW_OUTPUT>;

export type AnswerReviewInitiatedEventProps = BaseMixpanelEvent & {
  questionId: string;
  questionContent: string;
  answerId: string;
  answerContent: string;
  addedToFAQ?: boolean;
  questionOutput: AnswerReviewOutput;
  approvalModus: ApprovalModus;
};

export const APPROVAL_MODUS = {
  AUTOMATIC: 'automatic',
  MANUAL: 'manual',
} as const;

export type ApprovalModus = ObjectValues<typeof APPROVAL_MODUS>;

export type QuestionReviewInitiatedEventProps = BaseMixpanelEvent & {
  questionId: string;
  questionContent: string;
  response?: string;
  questionOutput: AnswerReviewOutput;
  posingQuestionSide?: QAndASides;
  approvalModus: ApprovalModus;
  questionAssignee: string;
};

export type MixpanelEventProps =
  | LogoutProps
  | ErrorViewedProps
  | PageViewedProps
  | DocumentEventProps
  | SearchInitiatedEventProps
  | NotificationViewedInitiatedEventProps
  | SecureLinkCreationInitiatedEventProps
  | LeadStatusUpdateInitiatedEventProps
  | ListingWatchedInitiatedEventProps
  | ListingVisibilityChangeInitiatedEventProps
  | ListingCreationInitiatedEventProps
  | MobileVerificationInitiatedEventProps
  | EnquiryCreationInitiatedEventProps
  | EnquiryWithdrawalInitiatedEventProps
  | SignupInitiatedProps
  | DealCreationInitiatedEventProps
  | SoftSignalInitiatedEventProps
  | QuickFileReleaseInitiatedEventProps
  | DocumentPublishedEventProps
  | QuestionSubmissionInitiatedEventProps
  | QuestionReplyInitiatedEventProps
  | AnswerReviewInitiatedEventProps
  | QuestionReviewInitiatedEventProps;
